import type { BlogPost } from '../types'

export const LACZENIE_FOTOWOLTAIKI_Z_POMPA_CIEPLA: BlogPost = {
  slug: 'laczenie-fotowoltaiki-z-pompa-ciepla',
  lang: 'pl',
  topicId: 'photovoltaik-waermepumpe-kombinieren',
  title: 'Łączenie fotowoltaiki z pompą ciepła: co to naprawdę daje',
  metaDescription:
    'Dlaczego fotowoltaika i pompa ciepła się uzupełniają, co to oznacza dla rachunków za prąd i na ' +
    'co zwrócić uwagę przy doborze wielkości. Stan na: lipiec 2026.',
  keywords: [
    'łączenie fotowoltaiki z pompą ciepła',
    'pompa ciepła z prądem solarnym',
    'autokonsumpcja fotowoltaika pompa ciepła',
    'porównaj oferty pompa ciepła',
    'koszt instalacji solarnej i pompy ciepła',
  ],
  publishedAt: '2026-07-21',
  excerpt:
    'Instalację solarną i pompę ciepła często rozpatruje się osobno - a tymczasem wzajemnie się ' +
    'wzmacniają: pompa ciepła staje się największym odbiorcą prądu w domu, instalacja fotowoltaiczna ' +
    'dostarcza dokładnie taki tani prąd własny. Co to oznacza dla planowania, doboru wielkości i kosztów.',
  sections: [
    {
      type: 'paragraph',
      text:
        'Fotowoltaikę i pompę ciepła przy składaniu zapytania traktuje się często osobno - najpierw ' +
        'instalacja solarna, później (lub niezależnie od niej) nowe ogrzewanie. Merytorycznie oba ' +
        'tematy są jednak ze sobą powiązane ściślej, niż sugeruje to takie oddzielne planowanie: pompa ' +
        'ciepła to zdecydowanie największy odbiorca prądu w domu jednorodzinnym, wyraźnie przed ' +
        'lodówką, pralką czy oświetleniem razem wziętymi. Instalacja fotowoltaiczna dostarcza dokładnie ' +
        'ten zasób, którego potrzebuje pompa ciepła: prąd. Kto planuje oba elementy razem, zamiast ' +
        'rozpatrywać je osobno, podejmuje ostatecznie lepsze decyzje ekonomiczne.',
    },
    {
      type: 'heading',
      text: 'Dlaczego fotowoltaika i pompa ciepła się uzupełniają',
    },
    {
      type: 'paragraph',
      text:
        'Pompa ciepła zamienia prąd na wielokrotnie większą ilość ciepła (sprawność, mierzona jako ' +
        'roczny współczynnik efektywności). To, czy ta energia pochodzi z sieci po aktualnej cenie ' +
        'prądu, czy z własnej instalacji solarnej, nie zmienia mocy grzewczej - zmienia za to wyraźnie ' +
        'koszt kilowatogodziny ciepła. Jednocześnie pompa ciepła jest dużym, stosunkowo elastycznym ' +
        'odbiorcą: przygotowanie ciepłej wody, a przy odpowiednio dużym zasobniku buforowym także ' +
        'część mocy grzewczej, można przesunąć na słoneczne godziny dnia, zamiast pracować sztywno ' +
        'według bieżącego zapotrzebowania. Właśnie w tej elastyczności tkwi prawdziwa wartość dodana ' +
        'połączenia: instalacja fotowoltaiczna zyskuje dużego, dobrze sterowalnego odbiorcę dla swojego ' +
        'prądu, zamiast oddawać południową nadwyżkę do sieci za niewielkie pieniądze.',
    },
    {
      type: 'heading',
      text: 'Wpływ na Państwa rachunki za prąd',
    },
    {
      type: 'paragraph',
      text:
        'Bez pompy ciepła typowa instalacja fotowoltaiczna domu jednorodzinnego pokrywa zwykle ' +
        'bezpośrednio tylko część rocznego zużycia prądu - duża część produkcji trafia w ciągu dnia do ' +
        'sieci, podczas gdy wieczorem i w nocy ponownie kupowany jest prąd sieciowy. Pompa ciepła ' +
        'wyraźnie zwiększa całkowite zużycie, ale też przesuwa profil zużycia: jeśli pracuje ' +
        '(automatycznie dzięki sterowaniu kompatybilnemu z instalacją lub ręcznie) najchętniej wtedy, ' +
        'gdy instalacja fotowoltaiczna dostarcza dużo prądu, udział prądu zużywanego na miejscu w ' +
        'całkowitym zużyciu (wskaźnik autokonsumpcji) wyraźnie rośnie. Każda kWh zużyta w ten sposób ' +
        'bezpośrednio, zamiast sprzedana po niskiej taryfie za nadwyżkę i później odkupiona drożej, ' +
        'poprawia opłacalność obu instalacji razem.',
    },
    {
      type: 'heading',
      text: 'Na co zwrócić uwagę przy łączeniu obu systemów',
    },
    {
      type: 'list',
      items: [
        'Dobierz wielkość instalacji fotowoltaicznej do rzeczywistego zapotrzebowania: roczne zużycie prądu z uwzględnieniem pompy ciepła (nie tylko dotychczasowe zużycie domowe bez ogrzewania) powinno wejść do obliczenia wielkości instalacji',
        'Sprawdź zasobnik buforowy/pojemność magazynowania cieplnego: odpowiednio duży zasobnik pozwala magazynować nadwyżkę fotowoltaiczną "termicznie", zamiast uruchamiać pompę ciepła wyłącznie dokładnie według bieżącego zapotrzebowania',
        'Porównaj taryfę dla pompy ciepła z bezpośrednim zużyciem własnym: obniżona taryfa dla pompy ciepła z osobnym licznikiem może się opłacać albo nie - zależy to od relacji między udziałem autokonsumpcji a różnicą taryf i wymaga indywidualnego obliczenia',
        'Zwróć uwagę na temperaturę zasilania i sposób rozprowadzania ciepła: ogrzewanie podłogowe pozwala na niższe temperatury zasilania, a tym samym na wydajniejszą pompę ciepła niż klasyczne grzejniki - wpływa to na to, na ile połączenie z fotowoltaiką w ogóle się opłaca',
        'Sprawdź osobno programy dofinansowań: dofinansowanie do fotowoltaiki i do pompy ciepła realizowane jest w ramach odrębnych programów z własnymi wymaganiami - niektóre gminy dodatkowo dofinansowują połączenie obu, ale nie jest to uregulowane jednolicie',
      ],
    },
    {
      type: 'heading',
      text: 'Najpierw pompa ciepła, najpierw fotowoltaika, czy planować razem?',
    },
    {
      type: 'paragraph',
      text:
        'Nie ma jednej uniwersalnie poprawnej kolejności - zależy to od stanu technicznego budynku, ' +
        'istniejącego ogrzewania i dostępnego budżetu. Tam, gdzie to możliwe, opłaca się jednak wspólne ' +
        'planowanie: odpowiednia wielkość instalacji fotowoltaicznej zależy w dużej mierze od tego, czy ' +
        'i jak pompa ciepła zmieni zużycie prądu. Jeśli instalacja fotowoltaiczna zostanie dobrana ' +
        'najpierw, bez uwzględnienia planowanej później pompy ciepła, później często okazuje się za ' +
        'mała, by pokryć istotną część nowego zużycia.',
    },
    {
      type: 'heading',
      text: 'Jak pomaga Roofora',
    },
    {
      type: 'paragraph',
      text:
        'W Roofora mogą Państwo zgłosić fotowoltaikę (system kompletny, sam montaż na dachu lub tylko ' +
        'instalację elektryczną) oraz pompę ciepła jako osobne, anonimowe zapytania i niezależnie ' +
        'porównać oferty z ceną stałą od zweryfikowanych firm - bez lawiny telefonów, z ujawnieniem ' +
        'kontaktu dopiero po Państwa aktywnej zgodzie dla konkretnej firmy. Dzięki temu mogą Państwo ' +
        'wystawić oba projekty osobno, ale planować je z myślą o ich połączeniu, zamiast wiązać się ' +
        'pochopnie z jednym dostawcą dla obu.',
    },
    {
      type: 'heading',
      text: 'Ważna uwaga',
    },
    {
      type: 'paragraph',
      text:
        'Wszystkie informacje w tym artykule odpowiadają stanowi z lipca 2026 roku i mają charakter ' +
        'ogólny - stawki dofinansowań, taryfy prądu i rzeczywista opłacalność połączenia obu systemów ' +
        'w dużej mierze zależą od konkretnego przypadku (budynek, zużycie, region). Ten artykuł nie ' +
        'zastępuje indywidualnego doradztwa energetycznego ani podatkowego.',
    },
  ],
  relatedLinks: [
    { label: 'Ile kosztuje instalacja solarna?', href: '/koszt-instalacji-fotowoltaicznej' },
    { label: 'Zaplanuj instalację krok po kroku', href: '/zaplanuj-instalacje-fotowoltaiczna' },
    { label: 'Fotowoltaika i pompa ciepła: porównaj oferty', href: '/fotowoltaika-pompa-ciepla' },
  ],
}
